'use client';

import { useEffect, useRef } from 'react';

/**
 * Counts a pull-stat up from 0 the first time it scrolls into view (the 99% /
 * 1,000+ editorial stats on the guide pages). SSR renders the final value, so
 * crawlers and prefers-reduced-motion users always see the real number; the
 * animation is a progressive enhancement layered on top.
 */
export default function MarketingCountUp({
  value,
  suffix = '',
  className,
}: {
  value: number;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const format = (n: number) => Math.round(n).toLocaleString('en-US');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Lock the rendered width so "0%" → "99%" doesn't shift neighboring text
    el.style.display = 'inline-block';
    el.style.minWidth = `${el.offsetWidth}px`;

    let raf = 0;
    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const duration = 1200;
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 4); // ease-out-quart
          el.textContent = format(value * eased) + suffix;
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.6 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, suffix]);

  return (
    <span ref={ref} className={className}>
      {format(value) + suffix}
    </span>
  );
}
